import { useEffect, useState, useCallback, useId, useRef, Suspense } from 'react'
import { Link, Outlet, useLocation, useParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import shopWebsiteService, { type ShopPage, type ShopWebsiteConfig } from '@/services/shopWebsiteService'
import { ShopFooter } from '@/components/shop/ShopFooter'
import { ShopStorefrontNav } from '@/components/shop/ShopStorefrontNav'
import { ShopStorefrontMobileNav } from '@/components/shop/ShopStorefrontMobileNav'
import { ShopLanguageSwitcher } from '@/components/shop/ShopLanguageSwitcher'
import { SHOP_NAV_FONT_STACK, shopStorefrontCanonicalPath, isStorefrontPaymentHandoffRoute } from '@/lib/shopStorefrontLayout'
import {
  parseShopDesignTokens,
  scopeShopCustomCss,
  shopDesignTokenCssVars,
} from '@/lib/shopDesignTokens'
import { flattenNavForFooter, navItemsFromPublishedPages } from '@/utils/shopStorefrontNav'
import {
  ShopStorefrontLocaleProvider,
  useShopStorefrontLocale,
} from '@/contexts/ShopStorefrontLocaleContext'
import { SHOP_STOREFRONT_LOCALE_STORAGE_KEY } from '@/i18n/shopStorefrontMessages'
import { useAuthStore } from '@/stores/authStore'
import { useShopPreviewToken } from '@/hooks/useShopPreviewToken'
import { ShopStorefrontHead } from '@/components/shop/ShopStorefrontHead'
import { ShopComingSoonPage } from '@/pages/public/ShopComingSoonPage'
import { useShopAnalytics } from '@/hooks/useShopAnalytics'
import { SupportChatWidget } from '@/components/support/SupportChatWidget'
import { ShopStorefrontAccountMenu } from '@/components/shop/ShopStorefrontAccountMenu'
import { shopPrimaryActionButtonStyle } from '@/utils/shopTheme'
import { rememberCustomerShopSlug } from '@/lib/customerShopReturn'
import { customDomainRedirectTarget } from '@/lib/shopPublicStorefrontUrl'

export type ShopStoreOutletContext = {
  slug: string
  config: ShopWebsiteConfig
  refreshCart: () => Promise<void>
  basePath: string
  previewToken: string | null
}

type ShopStoreLayoutContentProps = {
  slug: string
  basePath: string
}

/** Same stack as Tailwind `font-serif` and the logo — used when shop `font_family` is unset so PROD matches DEV (no reliance on Google CDN for Cormorant). */
const SHOP_DEFAULT_SERIF_STACK = "'Cormorant Garamond', Georgia, 'Times New Roman', serif"

const CART_SESSION_KEY = 'oms_cart_session'

const getCartSession = () => localStorage.getItem(CART_SESSION_KEY)

function ShopStoreLayoutContent({ slug, basePath }: ShopStoreLayoutContentProps) {
  const location = useLocation()
  const previewToken = useShopPreviewToken(slug)
  const { locale, localeResolved, t, setSupportedLocales } = useShopStorefrontLocale()
  const { isAuthenticated, user } = useAuthStore()
  const isCustomer = Boolean(isAuthenticated && user?.is_web_user)

  const [config, setConfig] = useState<ShopWebsiteConfig | null>(null)
  const [navPages, setNavPages] = useState<ShopPage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cartCount, setCartCount] = useState(0)
  const [headerSolidBg, setHeaderSolidBg] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const mobileNavPanelId = useId()
  const mergedCartSessionRef = useRef<string | null>(null)
  const { track } = useShopAnalytics(slug, previewToken)

  const refreshCart = useCallback(async () => {
    if (!slug) return
    try {
      const res = await shopWebsiteService.getCart(slug, getCartSession() || undefined, previewToken)
      const items = res.data?.items || []
      const count = items.reduce((sum, i) => sum + i.quantity, 0)
      setCartCount(count)
    } catch {
      setCartCount(0)
    }
  }, [slug, previewToken])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    setError(null)

    const loadConfig = async () => {
      try {
        const configRes = await shopWebsiteService.getBySlug(slug, previewToken)
        if (cancelled) return
        const next = configRes.data
        const redirectTo = customDomainRedirectTarget({
          domain: next?.domain,
          basePath,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        })
        if (redirectTo) {
          window.location.replace(redirectTo)
          return
        }
        setConfig(next)
        setSupportedLocales(next?.supported_content_locales)
        if (next?.storefront_access === 'coming_soon') {
          setLoading(false)
        }
      } catch {
        if (cancelled) return
        setError('Shop not found')
        setConfig(null)
        setNavPages([])
        setLoading(false)
      }
    }

    loadConfig()
    return () => {
      cancelled = true
    }
  }, [slug, previewToken, setSupportedLocales, basePath])

  useEffect(() => {
    if (!slug || !config || !localeResolved) return
    if (config.storefront_access === 'coming_soon') return
    let cancelled = false
    const showLayoutSpinner = navPages.length === 0

    const loadPages = async () => {
      if (showLayoutSpinner) setLoading(true)
      try {
        const pagesRes = await shopWebsiteService.getPages(slug, locale, previewToken)
        if (cancelled) return
        setNavPages(pagesRes.data || [])
        setError(null)
      } catch {
        if (cancelled) return
        setError('Shop not found')
        setNavPages([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadPages()
    return () => {
      cancelled = true
    }
  }, [slug, locale, config, localeResolved, navPages.length, previewToken])

  useEffect(() => {
    refreshCart()
  }, [refreshCart])

  useEffect(() => {
    if (!slug || !config || config.storefront_access === 'coming_soon') return
    track('page_view', { page_path: location.pathname })
  }, [slug, config, location.pathname, track])

  useEffect(() => {
    if (!slug || !isCustomer) return
    rememberCustomerShopSlug(slug)
  }, [slug, isCustomer])

  useEffect(() => {
    if (!slug || !isCustomer) return
    const sessionId = getCartSession()
    if (!sessionId) return
    const mergeKey = `${slug}:${sessionId}`
    if (mergedCartSessionRef.current === mergeKey) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await shopWebsiteService.mergeCart(slug, sessionId, previewToken)
        if (cancelled) return
        mergedCartSessionRef.current = mergeKey
        const merged = (res.data?.merged ?? 0) + (res.data?.added ?? 0)
        if (merged > 0) await refreshCart()
      } catch {
        /* guest merge is best-effort */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug, isCustomer, previewToken, refreshCart])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname, location.hash, location.search])

  useEffect(() => {
    if (!mobileNavOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mobileNavOpen])

  useEffect(() => {
    if (!location.hash) return
    const id = decodeURIComponent(location.hash.slice(1))
    if (!id) return
    const scrollTimer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
    return () => window.clearTimeout(scrollTimer)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (loading || error || !config) return

    const threshold = 8
    const onScroll = () => setHeaderSolidBg(window.scrollY > threshold)

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [loading, error, config, slug, location.pathname])

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !config) {
    return (
      <div className="mx-auto max-w-md rounded-2xl border border-red-100 bg-red-50 p-8 text-center text-red-600">
        <p className="font-medium">{error || 'Shop not found'}</p>
        <Link to="/shops" className="mt-4 inline-block text-sm underline">
          Browse all shops
        </Link>
      </div>
    )
  }

  if (config.storefront_access === 'coming_soon') {
    const isPaymentHandoff = isStorefrontPaymentHandoffRoute(location.pathname, basePath)
    if (isPaymentHandoff) {
      return (
        <div className="shop-storefront-root flex min-h-screen flex-col bg-slate-50">
          <ShopStorefrontHead config={config} canonicalPath={shopStorefrontCanonicalPath(basePath, slug)} />
          <main className="flex-1">
            <Suspense
              fallback={
                <div className="flex min-h-[40vh] items-center justify-center">
                  <LoadingSpinner />
                </div>
              }
            >
              <Outlet
                context={
                  { slug, config, refreshCart, basePath, previewToken } satisfies ShopStoreOutletContext
                }
              />
            </Suspense>
          </main>
        </div>
      )
    }
    return <ShopComingSoonPage config={config} basePath={basePath} />
  }

  const brandFontFamily =
    (config.design_tokens && parseShopDesignTokens(config.design_tokens).heading_font) ||
    (config.font_family && config.font_family.trim()) ||
    SHOP_DEFAULT_SERIF_STACK
  const designTokens = parseShopDesignTokens(config.design_tokens)
  const headerOverlayDark = designTokens.header_overlay === 'dark'
  const forceShowBrandName = designTokens.header_show_name === true
  const headerLogoSize = designTokens.header_logo_size || 'lg'
  const headerHideCart = designTokens.header_hide_cart === true
  const headerCtaText = (designTokens.header_cta_text || '').trim()
  const headerCtaLink = (designTokens.header_cta_link || '').trim()
  const headerBrandHtml = (designTokens.header_brand_html || '').trim()
  const catalogCardStyle = designTokens.catalog_card_style || 'classic'
  const tokenStyle = shopDesignTokenCssVars({
    primaryColor: config.primary_color,
    secondaryColor: config.secondary_color,
    fontFamily: config.font_family,
    designTokens,
  })
  const scopedCustomCss = scopeShopCustomCss(designTokens.custom_css || '')
  const navItems = navItemsFromPublishedPages(navPages, basePath)
  const footerNavLinks = flattenNavForFooter(navItems)
  const footerVariant = config.footer_variant ?? 'full'
  const headerNavCenter = config.header_nav_align === 'center'
  const pathNorm = (location.pathname.replace(/\/+$/, '') || '/')
  const baseNorm = (basePath.replace(/\/+$/, '') || '/')
  const isShopHome = pathNorm === baseNorm
  /** Dark chrome: translucent over cinematic home hero; solid black on inner pages / after scroll. */
  const headerDarkTransparent = headerOverlayDark && isShopHome && !headerSolidBg
  const headerDarkSolid = headerOverlayDark && !headerDarkTransparent
  const headerLightSolid = !headerOverlayDark && headerSolidBg
  const primary = config.primary_color || '#4f46e5'

  const logoSizeClass =
    headerLogoSize === 'sm'
      ? 'h-8 w-auto max-h-8 max-w-[2.5rem] sm:h-9 sm:max-h-9 sm:max-w-[2.75rem]'
      : headerLogoSize === 'md'
        ? 'h-10 w-auto max-h-10 max-w-[3.5rem] sm:h-11 sm:max-h-11 sm:max-w-[4rem]'
        : 'h-12 w-auto max-h-12 max-w-[min(10rem,34vw)] sm:h-16 sm:max-h-[4.5rem] sm:max-w-[min(16rem,38vw)] md:h-[4.75rem] md:max-h-[4.75rem]'

  const brandNameClass =
    headerLogoSize === 'sm'
      ? 'text-base font-semibold leading-tight tracking-tight sm:text-lg'
      : headerLogoSize === 'md'
        ? 'text-lg font-semibold leading-tight tracking-tight sm:text-xl'
        : 'text-lg font-semibold leading-[1.12] tracking-tight sm:text-xl lg:text-2xl lg:font-bold xl:text-[1.75rem] xl:leading-[1.1]'

  const resolveHeaderHref = (link: string) => {
    const raw = link.trim()
    if (/^https?:\/\//i.test(raw)) return raw
    return raw.startsWith('/') ? `${basePath}${raw}` : `${basePath}/${raw}`
  }

  const brand = (
    <Link
      to={basePath}
      className="flex min-h-0 min-w-0 max-w-full items-center gap-2 py-0.5 sm:gap-2.5 md:gap-3"
      data-storefront-brand=""
    >
      {config.logo_url ? (
        <img
          src={config.logo_url}
          alt=""
          className={`${logoSizeClass} shrink-0 object-contain object-left`}
        />
      ) : null}
      <span
        className={`shop-storefront-brand-name min-w-0 flex-1 truncate ${brandNameClass} ${
          config.logo_url && !forceShowBrandName ? 'hidden xl:inline' : ''
        }`}
        style={{
          fontFamily: brandFontFamily,
          color: headerOverlayDark ? '#f8fafc' : primary,
        }}
        {...(headerBrandHtml
          ? {
              dangerouslySetInnerHTML: {
                __html: DOMPurify.sanitize(headerBrandHtml, { ADD_ATTR: ['class'] }),
              },
            }
          : { children: config.name })}
      />
    </Link>
  )

  const headerCta =
    headerCtaText && headerCtaLink ? (
      <Link
        to={resolveHeaderHref(headerCtaLink)}
        data-storefront-header-cta=""
        className="inline-flex shrink-0 items-center justify-center rounded-full px-4 py-2 text-xs font-semibold tracking-tight text-stone-950 shadow-[0_0_24px_rgba(201,162,39,0.35)] transition hover:brightness-110 sm:px-5 sm:py-2.5 sm:text-sm"
        style={{
          background: `linear-gradient(135deg, ${primary} 0%, color-mix(in srgb, ${primary} 72%, white) 100%)`,
        }}
      >
        {headerCtaText}
      </Link>
    ) : null

  const cartBtn = headerHideCart ? null : (
    <Link
      to={`${basePath}/cart`}
      className="relative inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold tracking-tight transition hover:brightness-95 sm:px-3 sm:py-1.5 sm:text-sm"
      style={shopPrimaryActionButtonStyle(primary)}
    >
      {t('header.cart')}
      {cartCount > 0 && (
        <span className="flex min-w-[1.25rem] items-center justify-center rounded-full bg-white/90 px-1.5 py-0.5 text-xs font-semibold text-slate-800">
          {cartCount > 99 ? '99+' : cartCount}
        </span>
      )}
    </Link>
  )

  const mainNav = (
    <nav className="shop-storefront-header-nav min-w-0">
      <ShopStorefrontNav items={navItems} primaryColor={primary} />
    </nav>
  )

  const authLinks = (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
      {isCustomer ? (
        <ShopStorefrontAccountMenu shopSlug={slug} primaryColor={primary} />
      ) : (
        <Link
          to={`/auth/customer/login?shop=${encodeURIComponent(slug)}`}
          state={{ from: { pathname: location.pathname, search: location.search || '' } }}
          className="rounded-md px-1.5 py-0.5 text-[13px] font-medium tracking-tight underline decoration-2 underline-offset-4 transition-opacity hover:opacity-85 md:text-sm"
          style={{ color: primary }}
        >
          {t('header.login')}
        </Link>
      )}
    </div>
  )

  const rowUtility = (
    <div
      className="flex flex-wrap items-center justify-end gap-2 sm:gap-3"
      data-storefront-utility=""
    >
      <ShopLanguageSwitcher primaryColor={primary} />
      {authLinks}
    </div>
  )

  const rowNavAndCart = headerNavCenter ? (
    <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-y-1 py-1.5 sm:py-2">
      <span className="min-w-0" aria-hidden="true" />
      <div className="flex min-w-0 justify-center px-2">{mainNav}</div>
      <div className="flex min-w-0 items-center justify-end gap-2 sm:gap-3">
        {cartBtn}
        {headerCta}
      </div>
    </div>
  ) : (
    <div className="flex w-full min-w-0 flex-nowrap items-center justify-end gap-x-3 md:gap-x-4 py-1.5 sm:py-2">
      {mainNav}
      {cartBtn}
      {headerCta}
    </div>
  )

  const headerBackgroundColor = headerDarkTransparent
    ? 'rgba(8,8,10,0.28)'
    : headerDarkSolid
      ? '#0c0c0e'
      : headerLightSolid
        ? '#ffffff'
        : config.primary_color
          ? `color-mix(in srgb, ${primary} 8%, white)`
          : 'rgba(255,255,255,0.88)'

  return (
    <div
      className="shop-storefront-root min-h-screen bg-slate-50"
      style={tokenStyle}
      data-header-overlay={headerOverlayDark ? 'dark' : undefined}
      data-header-solid={headerLightSolid ? 'true' : 'false'}
      data-header-tone={headerOverlayDark ? 'dark' : 'light'}
      data-catalog-card-style={catalogCardStyle}
    >
      {scopedCustomCss ? (
        <style data-shop-custom-css="true" dangerouslySetInnerHTML={{ __html: scopedCustomCss }} />
      ) : null}
      {catalogCardStyle !== 'classic' ? (
        <style
          data-shop-catalog-card-style="true"
          dangerouslySetInnerHTML={{
            __html: `
.shop-storefront-root[data-catalog-card-style="soft"] .shop-product-card{
  border-color: transparent !important;
  background: #f4f4f5 !important;
  box-shadow: none !important;
  border-radius: 1rem !important;
}
.shop-storefront-root[data-catalog-card-style="soft"] .shop-product-card:hover{
  background: #ececef !important;
  box-shadow: none !important;
}
.shop-storefront-root[data-catalog-card-style="soft"] .shop-product-card a.aspect-square{
  background: transparent !important;
}
.shop-storefront-root[data-catalog-card-style="soft"] .shop-product-card-body{
  background: transparent;
  padding-top: 0.75rem !important;
}
.shop-storefront-root[data-catalog-card-style="dense"] .shop-product-card{
  border-radius: 0.5rem !important;
  border-color: #e2e8f0 !important;
  box-shadow: 0 1px 2px rgba(15,23,42,0.08) !important;
}
.shop-storefront-root[data-catalog-card-style="dense"] .shop-product-card-body{
  padding: 0.75rem !important;
}
.shop-storefront-root[data-catalog-card-style="flat"] .shop-product-card{
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}
.shop-storefront-root[data-catalog-card-style="flat"] .shop-product-card:hover{
  box-shadow: none !important;
}
.shop-storefront-root[data-catalog-card-style="flat"] .shop-product-card-body{
  padding-left: 0 !important;
  padding-right: 0 !important;
}
`.trim(),
          }}
        />
      ) : null}
      {headerOverlayDark ? (
        <style
          data-shop-header-overlay="true"
          dangerouslySetInnerHTML={{
            __html: `
.shop-storefront-root[data-header-overlay="dark"] [data-hero-layout="cinematic"]{
  margin-top: calc(-1 * var(--shop-header-overlay-offset, 6.75rem));
}
.shop-storefront-root[data-header-tone="dark"] .shop-storefront-header-nav a,
.shop-storefront-root[data-header-tone="dark"] [data-storefront-utility] a{
  color: rgba(255,255,255,0.92) !important;
}
.shop-storefront-root[data-header-tone="dark"] .shop-storefront-header-nav a[aria-current="page"],
.shop-storefront-root[data-header-tone="dark"] .shop-storefront-header-nav a:hover{
  color: var(--shop-primary) !important;
}
.shop-storefront-root[data-header-tone="dark"] .shop-storefront-brand-name{
  color: #f8fafc;
}
.shop-storefront-root[data-header-tone="dark"] .shop-brand-accent{
  color: var(--shop-primary);
}
`.trim(),
          }}
        />
      ) : null}
      <ShopStorefrontHead config={config} canonicalPath={shopStorefrontCanonicalPath(basePath, slug)} />
      {config.is_preview ? (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900">
          Preview mode — this shop is not published. Only people with this link can view it.
        </div>
      ) : null}
      <header
        data-storefront-header="compact-v2"
        className={`sticky top-0 z-50 backdrop-blur-md transition-[background-color,box-shadow,border-color,backdrop-filter,color] duration-300 ease-out ${
          headerLightSolid
            ? 'border-b border-slate-200/80 bg-white/90 shadow-sm'
            : headerDarkSolid
              ? 'border-b border-white/10 shadow-sm'
              : 'border-b border-transparent shadow-none'
        }`}
        style={{
          backgroundColor: headerLightSolid ? undefined : headerBackgroundColor,
          ['--shop-primary' as string]: primary,
          ['--shop-nav-font-family' as string]: SHOP_NAV_FONT_STACK,
          color: headerOverlayDark ? 'rgba(255,255,255,0.92)' : undefined,
        }}
      >
        <div className="container mx-auto px-4 sm:px-6">
          {/* Below md: stacked header — avoids nav flex-wrap overlapping the logo in a narrow grid cell */}
          <div className="flex flex-col gap-3 py-3 lg:hidden">
            <div className="flex min-h-10 items-center justify-end">{rowUtility}</div>
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="min-w-0 flex-1">{brand}</div>
              <div className="flex shrink-0 items-center gap-2">
                {headerCta}
                {cartBtn}
                <button
                  type="button"
                  className={`rounded-lg p-2 transition-colors hover:bg-black/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--shop-primary)] focus-visible:ring-offset-2 ${
                    headerOverlayDark ? 'text-white hover:bg-white/10' : 'text-slate-800'
                  }`}
                  aria-expanded={mobileNavOpen}
                  aria-controls={mobileNavPanelId}
                  aria-label={mobileNavOpen ? t('header.closeMenu') : t('header.openMenu')}
                  onClick={() => setMobileNavOpen((o) => !o)}
                >
                  {mobileNavOpen ? (
                    <XMarkIcon className="h-7 w-7" aria-hidden />
                  ) : (
                    <Bars3Icon className="h-7 w-7" aria-hidden />
                  )}
                </button>
              </div>
            </div>
            {mobileNavOpen ? (
              <div
                id={mobileNavPanelId}
                className="-mx-4 border-t border-slate-200/80 px-4 pb-1 pt-3 sm:-mx-6 sm:px-6"
              >
                <ShopStorefrontMobileNav
                  items={navItems}
                  primaryColor={primary}
                  onNavigate={() => setMobileNavOpen(false)}
                />
              </div>
            ) : null}
          </div>

          <div className="hidden lg:grid lg:grid-cols-[auto_minmax(0,1fr)] lg:gap-x-5 xl:gap-x-6">
            <div className="row-span-2 flex min-w-0 items-center py-0.5">
              {brand}
            </div>
            <div className="flex min-h-[2.25rem] items-center justify-end py-1.5">
              {rowUtility}
            </div>
            <div className="min-w-0">{rowNavAndCart}</div>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Suspense
          fallback={
            <div className="flex min-h-[40vh] items-center justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <Outlet
            context={
              { slug, config, refreshCart, basePath, previewToken } satisfies ShopStoreOutletContext
            }
          />
        </Suspense>
      </main>

      <ShopFooter
        variant={footerVariant}
        navLinks={footerNavLinks}
        config={{
          name: config.name,
          logo_url: config.logo_url,
          address: config.address,
          phone: config.phone,
          email: config.email,
          facebook_url: config.facebook_url,
          whatsapp_url: config.whatsapp_url,
        }}
        basePath={basePath}
      />
      <SupportChatWidget enabled={config.support_chat_enabled !== false} shopSlug={slug} />
    </div>
  )
}

type ShopStoreLayoutProps = {
  slugOverride?: string
  basePathOverride?: string
}

const ShopStoreLayout = ({ slugOverride, basePathOverride }: ShopStoreLayoutProps) => {
  const { slug: paramSlug } = useParams<{ slug: string }>()
  const slug = slugOverride || paramSlug || ''
  const basePath = basePathOverride ?? (slug ? `/shop/${slug}` : '')
  const localeStorageKey = slug
    ? `${SHOP_STOREFRONT_LOCALE_STORAGE_KEY}:${slug}`
    : SHOP_STOREFRONT_LOCALE_STORAGE_KEY

  return (
    <ShopStorefrontLocaleProvider key={slug} localeStorageKey={localeStorageKey}>
      <ShopStoreLayoutContent slug={slug} basePath={basePath} />
    </ShopStorefrontLocaleProvider>
  )
}

export default ShopStoreLayout
